import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { MarketingWifiService } from 'src/shared/services/marketing-wifi.service';
import { ConciergeQueriesService } from 'src/shared/services/concierge-queries.service';
import { CreateConciergeItemData } from 'src/shared/services/concierge-api.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'order-sensor',
  templateUrl: './order-sensor.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OrderSensorComponent implements OnInit {
  private readonly navCtrl = inject(CustomNavController);
  private readonly storage = inject(TidyStorage);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly addressesQueries = inject(AddressesQueriesService);
  private readonly wifiService = inject(MarketingWifiService);
  private readonly conciergeQueries = inject(ConciergeQueriesService);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  readonly windowService = inject(WindowService);

  form = this.fb.group({
    property: [null as number | null, Validators.required],
    quantity: [1],
  });
  submitted = false;
  isSubmitting = false;
  errorMessage = '';
  propertyItems: { value: number; viewValue: string }[] = [];
  quantityItems = [1, 2, 3, 4, 5].map(n => ({ value: n, viewValue: n.toString() }));
  addToAllMissing = false;
  propertiesMissingCount = 0;
  sourceContext: 'wifi' | 'noise' | 'hardware' = 'wifi';

  boundSubmitOrder = this.submitOrder.bind(this);

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Order Sensor');
    this.sourceContext = this.navCtrl.getParam('sourceContext', false) || 'wifi';
    if (!this.sourceContext) {
      const dialogParams = await this.storage.retrieve('dialogParams');
      this.sourceContext = dialogParams?.sourceContext || 'wifi';
    }

    const addresses = await this.addressesQueries.getAddresses();
    this.propertyItems = addresses.map(a => ({
      value: a.id,
      viewValue: a.address_name || a.address1 || `Property ${a.id}`,
    }));

    const allIds = addresses.map(a => a.id);
    const missingIds = this.wifiService.getPropertiesWithoutSensor(allIds);
    this.propertiesMissingCount = missingIds.length;

    this.cdr.markForCheck();
  }

  async submitOrder() {
    if (this.demoBlock.block()) { return; }
    this.submitted = true;
    this.errorMessage = '';

    if (!this.addToAllMissing && !this.form.value.property) {
      this.errorMessage = 'Please select a property.';
      this.cdr.markForCheck();
      return;
    }

    this.isSubmitting = true;
    this.cdr.markForCheck();

    try {
      if (this.addToAllMissing) {
        const addresses = await this.addressesQueries.getAddresses();
        const allIds = addresses.map(a => a.id);
        const missingIds = this.wifiService.getPropertiesWithoutSensor(allIds);
        const propertyNames = missingIds.map(id => {
          const addr = addresses.find(a => a.id === id);
          return addr?.address_name || addr?.address1 || `Property ${id}`;
        });

        const data: CreateConciergeItemData = {
          type: 'support.concierge_task',
          data: {
            subject: `Order TIDY Sensor for ${missingIds.length} properties`,
            body: `Please send one TIDY Sensor to each of the following properties:\n\n${propertyNames.join('\n')}\n\nTotal sensors needed: ${missingIds.length}`,
            attachments: [],
            emails: [],
          },
          metadata: {
            order_type: 'tidy_sensor_bulk',
            property_ids: missingIds,
            quantity_per_property: 1,
            total_quantity: missingIds.length,
          },
        };
        await this.conciergeQueries.createItemMutation.mutateAsync(data);
      } else {
        const selectedProperty = this.propertyItems.find(p => p.value === this.form.value.property);
        const quantity = this.form.value.quantity || 1;

        const data: CreateConciergeItemData = {
          type: 'support.concierge_task',
          data: {
            subject: `Order TIDY Sensor (x${quantity}) for ${selectedProperty?.viewValue}`,
            body: `Please send ${quantity} TIDY Sensor(s) to: ${selectedProperty?.viewValue}\n\nDevice: TIDY Wi-Fi & Noise Sensor\nQuantity: ${quantity}`,
            attachments: [],
            emails: [],
          },
          metadata: {
            order_type: 'tidy_sensor',
            property_id: this.form.value.property,
            quantity,
          },
        };
        await this.conciergeQueries.createItemMutation.mutateAsync(data);
      }

      this.util.showSuccess('Sensor order submitted!');
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateBack(this.sourceContext === 'noise' ? 'compliance' : 'revenue');
      }
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to submit order. Please try again.';
    } finally {
      this.isSubmitting = false;
      this.cdr.markForCheck();
    }
  }

  goBack() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.goBack();
    } else {
      this.navCtrl.navigateBack(this.sourceContext === 'noise' ? 'compliance' : 'revenue');
    }
  }
}
